import React from 'react';

/**
 * Returns a setter for a single field of an object held in React state. Lets
 * callers do `<Input onChange={setName} />` without rebuilding a closure
 * per render and without `@ts-ignore` for dynamic key access.
 *
 * Example:
 * ```ts
 * const [user, setUser] = useState<User | undefined>(undefined);
 * const setEmail = useObjectField(user, setUser, 'email');
 * ```
 */
export function useObjectField<
  T extends Record<string, any>,
  K extends keyof T,
>(
  object: T | undefined | null,
  setObject: React.Dispatch<React.SetStateAction<T>>,
  key: K
): (next: T[K] | ((prev: T[K] | undefined) => T[K])) => void {
  const objectRef = React.useRef(object);
  objectRef.current = object;

  return React.useCallback(
    (next: T[K] | ((prev: T[K] | undefined) => T[K])) => {
      const current = objectRef.current;
      const resolved =
        typeof next === 'function'
          ? (next as (prev: T[K] | undefined) => T[K])(current?.[key])
          : next;
      setObject(prev => ({ ...prev, [key]: resolved }) as T);
    },
    [setObject, key]
  );
}
